import { DealDataExport } from '../../utils/export/deal-data-export';
import { DataExport } from '../../utils/export/data-export';

import DealExportColumn = DealDataExport.DealExportColumn;
import dealDateValueProvider = DealDataExport.dealDateValueProvider;
import bidStageValueProvider = DealDataExport.bidStageValueProvider;
import sizeValueProvider = DealDataExport.sizeValueProvider;
import sizeFormatProvider = DataExport.sizeFormatProvider;
import issuerValueProvider = DealDataExport.issuerValueProvider;
import issueDescriptionValueProvider = DealDataExport.issueDescriptionValueProvider;
import bidSubmissionValueProvider = DealDataExport.bidSubmissionValueProvider;
import StringValueGetterDealValueProvider = DealDataExport.StringValueGetterDealValueProvider;
import RatingsBySeriesExportValueProvider = DealDataExport.RatingsBySeriesExportValueProvider;
import enhancementValueProvider = DealDataExport.enhancementValueProvider;
import ropValueProvider = DealDataExport.ropValueProvider;
import esgValueProvider = DealDataExport.esgValueProvider;
import purposeValueProvider = DealDataExport.purposeValueProvider;
import dealStructureValueProvider = DealDataExport.dealStructureValueProvider;
import seniorManagerValueProvider = DealDataExport.seniorManagerValueProvider;
import erpValueProvider = DealDataExport.erpValueProvider;
import sourceOfRepaymentValueProvider = DealDataExport.sourceOfRepaymentValueProvider;
import bankQualifiedValueProvider = DealDataExport.bankQualifiedValueProvider;
import issueTypeValueProvider = DealDataExport.issueTypeValueProvider;
import firstCallDateValueProvider = DealDataExport.firstCallDateValueProvider;
import firstCouponDateValueProvider = DealDataExport.firstCouponDateValueProvider;
import datedDateValueProvider = DealDataExport.datedDateValueProvider;
import dealStatusValueProvider = DealDataExport.dealStatusValueProvider;
import dealTypeValueProvider = DealDataExport.dealTypeValueProvider;
import parityPlusValueProvider = DealDataExport.parityPlusValueProvider;

import { stateValueGetter } from '../../grid/utils/value-getters/state';
import {
  filterEmptyValues,
  getEmptyToDashesValueMapper,
  getJoinWithSeparatorValueMapper,
  getMomentsFormatter,
  removeDuplicationsValueMapper
} from '../../utils/formatters/value-mappers';
import { RatingAgency, RATING_AGENCIES } from '../../helpers/issue/ratings';
import { taxStatusGetter } from '../../grid/utils/value-getters/tax-status';
import { dealTypeValueGetter } from '../../grid/utils/value-getters/deal-type';
import { sectorValueGetter } from '../../grid/utils/value-getters/sector';
import { ExcelColumns } from '../../types/excel-columns.types';

export namespace DealColumnsExport {
  export function getMulitpleColumns(
    columnNames: string[]
  ): DealExportColumn[] {
    const exportConfig: DealExportColumn[] = [];
    columnNames.forEach((columnName) => {
      if (columnName === ExcelColumns.ratings) {
        exportConfig.push(...getRatingsColumns(RATING_AGENCIES));
      } else {
        exportConfig.push(getExportColumn(columnName));
      }
    });

    return exportConfig;
  }

  export function getExportColumn(columnName: string): DealExportColumn {
    switch (columnName) {
      case ExcelColumns.date:
        return new DealExportColumn(columnName, dealDateValueProvider);

      case ExcelColumns.bidStage:
        return new DealExportColumn(columnName, bidStageValueProvider);

      case ExcelColumns.par:
        return new DealExportColumn(columnName, sizeValueProvider, {
          formatProvider: sizeFormatProvider
        });

      case ExcelColumns.issuer:
        return new DealExportColumn(columnName, issuerValueProvider);

      case ExcelColumns.issueDescription:
        return new DealExportColumn(columnName, issueDescriptionValueProvider);

      case ExcelColumns.state:
        return new DealExportColumn(
          columnName,
          new StringValueGetterDealValueProvider(stateValueGetter),
          {
            mappers: [getJoinWithSeparatorValueMapper()]
          }
        );

      case ExcelColumns.bidSubmission:
        return new DealExportColumn(columnName, bidSubmissionValueProvider);

      case ExcelColumns.enhancement:
        return new DealExportColumn(columnName, enhancementValueProvider, {
          mappers: [getJoinWithSeparatorValueMapper()]
        });

      case ExcelColumns.taxStatus:
        return new DealExportColumn(
          columnName,
          new StringValueGetterDealValueProvider(taxStatusGetter),
          {
            mappers: [getJoinWithSeparatorValueMapper()]
          }
        );

      case ExcelColumns.type:
        return new DealExportColumn(
          columnName,
          new StringValueGetterDealValueProvider(dealTypeValueGetter),
          {
            mappers: [getJoinWithSeparatorValueMapper()]
          }
        );

      case ExcelColumns.rop:
        return new DealExportColumn(columnName, ropValueProvider);

      case ExcelColumns.esg:
        return new DealExportColumn(columnName, esgValueProvider);

      case ExcelColumns.sector:
        return new DealExportColumn(
          columnName,
          new StringValueGetterDealValueProvider(sectorValueGetter),
          {
            mappers: [getJoinWithSeparatorValueMapper()]
          }
        );

      case ExcelColumns.purpose:
        return new DealExportColumn(columnName, purposeValueProvider, {
          mappers: [
            removeDuplicationsValueMapper,
            getJoinWithSeparatorValueMapper()
          ]
        });

      case ExcelColumns.structure:
        return new DealExportColumn(columnName, dealStructureValueProvider);

      case ExcelColumns.leadManager:
        return new DealExportColumn(columnName, seniorManagerValueProvider);

      case ExcelColumns.erp:
        return new DealExportColumn(columnName, erpValueProvider);

      case ExcelColumns.sourceOfRepayment:
        return new DealExportColumn(
          columnName,
          sourceOfRepaymentValueProvider,
          {
            mappers: [
              removeDuplicationsValueMapper,
              getJoinWithSeparatorValueMapper()
            ]
          }
        );

      case ExcelColumns.bankQualified:
        return new DealExportColumn(columnName, bankQualifiedValueProvider, {
          mappers: [
            removeDuplicationsValueMapper,
            getJoinWithSeparatorValueMapper()
          ]
        });

      case ExcelColumns.issueType:
        return new DealExportColumn(columnName, issueTypeValueProvider, {
          mappers: [
            removeDuplicationsValueMapper,
            getJoinWithSeparatorValueMapper()
          ]
        });

      case ExcelColumns.firstCallDate:
        return new DealExportColumn(columnName, firstCallDateValueProvider);

      case ExcelColumns.firstCouponDate:
        return new DealExportColumn(columnName, firstCouponDateValueProvider, {
          mappers: [
            filterEmptyValues,
            getMomentsFormatter(),
            getJoinWithSeparatorValueMapper()
          ]
        });

      case ExcelColumns.datedDate:
        return new DealExportColumn(columnName, datedDateValueProvider, {
          mappers: [
            filterEmptyValues,
            getMomentsFormatter(),
            getJoinWithSeparatorValueMapper()
          ]
        });
      case ExcelColumns.parityPlus:
        return new DealExportColumn(columnName, parityPlusValueProvider);

      case ExcelColumns.dealStatus:
        return new DealExportColumn(columnName, dealStatusValueProvider);

      case ExcelColumns.offeringType:
        return new DealExportColumn(columnName, dealTypeValueProvider);
    }
    return null;
  }

  export function getRatingsColumns(
    agencies: RatingAgency[]
  ): DealExportColumn[] {
    return agencies.map(
      (agency) =>
        new DealExportColumn(
          agency.name,
          new RatingsBySeriesExportValueProvider(agency.name),
          {
            mappers: [
              getEmptyToDashesValueMapper(),
              getJoinWithSeparatorValueMapper()
            ]
          }
        )
    );
  }
}
